import { IExecutionOptions, IQueryOptions, IQueryKeyValue, IQueryContext, IQueryResponseMapping, IQueryStaticMethodOptions, DebugType, IQueryContextConfiguration } from '../models/query';
import { TranslationHelper } from '../helpers/TranslationHelper';

import { isUndefined } from '../utils/typeChecks';
import { deepMergeObjects } from '../utils/object';
import { isArrayLike } from '../utils/isArrayLike';
import isQueryOptionsObject from '../utils/isQueryOptionsObject';

import { BaseQuery } from './BaseQuery';
import { PropertyQuery } from './PropertyQuery';
import { AllocationsQuery } from './AllocationsQuery';
import { StatisticsQuery } from './StatisticsQuery';
import { TimeseriesQuery } from './TimeseriesQuery';
import { FundManagerQuery } from './FundManagerQuery';
import { FundManagersQuery } from './FundManagersQuery';
import { LoggedInUserQuery } from './LoggedInUserQuery';
import { CommentaryQuery } from './CommentaryQuery';
import { DisclaimerQuery } from './DisclaimerQuery';
import { DocumentQuery } from './DocumentQuery';
import { SingleDocumentQuery } from './SingleDocumentQuery';
import { ResponseQuery } from './ResponseQuery';
import { ValueQuery } from './ValueQuery';
import { DynamicQuery } from './DynamicQuery';
import { InputQuery } from './InputQuery';
import { StatisticQuery } from './StatisticQuery';
import { ObjectQuery } from './ObjectQuery';
import { GlobalQuery } from './GlobalQuery';
import { FundListsQuery } from './FundListsQuery';
import { ClientConfigurationQuery } from './ClientConfigurationQuery';
import { UrlQuery } from './UrlQuery';
import { ContextStore } from '../stores/ContextStore';
import { WindowQuery } from './WindowQuery';
import { JoinQuery } from './JoinQuery';
import { ScenarioQuery } from './ScenarioQuery';
import { PortfolioQuery } from './PortfolioQuery';
import { PortfolioEntityQuery } from './PortfolioEntityQuery';
import { AssetQuery } from './AssetQuery';
import { ScenarioReportQuery } from './ScenarioReportQuery';
import { ScenarioStatisticQuery } from './ScenarioStatisticQuery';
import { EntitiesQuery } from './EntitiesQuery';
import { BreakdownAnalysisQuery } from './BreakdownAnalysisQuery';
import { IContextAndConfiguration } from '../models/context';
import { ExternalApiQuery } from './externalApi/ExternalApiQuery';
import { TransactionsQuery } from './TransactionsQuery';

export const CONTEXT_STORE_WINDOW_KEY: string = '__ksysContextStore__';

export class Query {
	constructor(public options: IQueryOptions, public translationHelper?: TranslationHelper, public callStackLevel?: number) { }
	static get contextStore(): ContextStore {
		if (!(window as any)[CONTEXT_STORE_WINDOW_KEY]) {
			(window as any)[CONTEXT_STORE_WINDOW_KEY] = new ContextStore();
		}
		return (window as any)[CONTEXT_STORE_WINDOW_KEY];
	}
	static getContextAndConfiguration(executionOptions: IExecutionOptions, queryOptions: IQueryOptions): IContextAndConfiguration | undefined {
		if (queryOptions) {
			const { defaultContextParentKey } = executionOptions;
			const { contextParentKey = defaultContextParentKey, contextKey = 'default' } = queryOptions;
			const contextStore = executionOptions.contextStore || Query.contextStore;
			if (contextStore) {
				const contextAndConfiguration = contextStore.get({ contextParentKey, contextKey });
				return contextAndConfiguration;
			}
		}
		return undefined;
	}
	static getContext(executionOptions: IExecutionOptions, queryOptions: IQueryOptions): IQueryContext | undefined {
		if (executionOptions) {
			const { context } = executionOptions;
			if (context) {
				return context;
			}
		}
		if (queryOptions) {
			const contextAndConfiguration = this.getContextAndConfiguration(executionOptions, queryOptions);
			return contextAndConfiguration && contextAndConfiguration.context;
		}
		return undefined;
	}
	static getContextConfiguration(executionOptions: IExecutionOptions, queryOptions: IQueryOptions): IQueryContextConfiguration | undefined {
		if (executionOptions) {
			const { contextConfiguration } = executionOptions;
			if (contextConfiguration) {
				return contextConfiguration;
			}
		}
		if (queryOptions) {
			const contextAndConfiguration = this.getContextAndConfiguration(executionOptions, queryOptions);
			return contextAndConfiguration && contextAndConfiguration.contextConfiguration;
		}
		return undefined;
	}
	static convertQueryKeyValue(queryKeyValue: IQueryKeyValue, executionOptions: IExecutionOptions, staticOptions?: IQueryStaticMethodOptions) {
		const response: { [key: string]: any } = {};
		if (queryKeyValue) {
			Object.keys(queryKeyValue).forEach((key) => {
				const valueQueryOptions = queryKeyValue[key];
				if (valueQueryOptions) {
					const valueQuery = new Query(valueQueryOptions, staticOptions && staticOptions.translationHelper);
					const value = valueQuery.execute(executionOptions);
					response[key] = value;
				}
			});
		}
		return response;
	}
	static convertQueryMime(queryMime: any, executionOptions: IExecutionOptions, staticOptions?: IQueryStaticMethodOptions) {
		const response: object = {};
		if (typeof queryMime === 'object') {
			const { _queryResponseMappings } = queryMime;
			const keys = Object.keys(queryMime);
			if (_queryResponseMappings) {
				for (const queryResponseMapping of (_queryResponseMappings as IQueryResponseMapping[])) {
					Query.loadQueryResponseMapping(queryResponseMapping, response, queryMime, executionOptions, keys, staticOptions);
				}
			}
			keys.forEach((key) => {
				Query.loadQueryMimeKey(response, key, queryMime, executionOptions, staticOptions);
			});
			return response;
		}
		return queryMime;
	}
	static loadQueryResponseMapping(queryResponseMapping: IQueryResponseMapping, response: object, queryMime: any, executionOptions: IExecutionOptions, keys: string[], staticOptions?: IQueryStaticMethodOptions) {
		const { key, children } = queryResponseMapping;
		Query.loadQueryMimeKey(response, key, queryMime, executionOptions, staticOptions);
		const keyIndex = keys.indexOf(key);
		if (keyIndex > -1) {
			keys.splice(keyIndex, 1);
		}
		const parentResponse = response[key];
		if (children && isArrayLike(children) && children.length > 0) {
			for (const child of children) {
				Query.loadQueryResponseMapping(child, response, queryMime, { ...executionOptions, responseContext: parentResponse }, keys, staticOptions);
			}
		}
	}
	static loadQueryMimeKey(response: object, key: string, queryMime: any, executionOptions: IExecutionOptions, staticOptions?: IQueryStaticMethodOptions) {
		const value = queryMime[key];
		if (value) {
			if (isArrayLike(value)) {
				response[key] = (value as any[]).map((item) => {
					return Query.convertQueryMime(item, executionOptions, staticOptions);
				});
			}
			else if (typeof value === 'object') {
				if (value.queryOptionsType) {
					const valueQuery = new Query(value as IQueryOptions, staticOptions && staticOptions.translationHelper);
					const responseValue = valueQuery.execute(executionOptions);
					response[key] = responseValue;
				}
				else {
					response[key] = Query.convertQueryMime(value as any, executionOptions, staticOptions);
				}
			}
			else {
				response[key] = value;
			}
		}
	}
	mergeExecutionOptions(...args: (IExecutionOptions | undefined)[]) {
		let response: IExecutionOptions = {};
		for (const arg of args) {
			if (arg) {
				const { context: fullContext, ...executionOptions } = arg;
				const { entityByType, ...context } = (fullContext || {}) as IQueryContext;
				const hasContext = typeof context === 'object' && Object.keys(context).length > 0;
				response = deepMergeObjects(response, executionOptions, hasContext ? { context } : {});
				if (entityByType) {
					if (!response.context) {
						response.context = {};
					}
					response.context.entityByType = entityByType;
				}
			}
		}
		return response;
	}
	private debugBase(type: DebugType, ...messages: any[]) {
		if (this.options && this.options.debug && this.options.debug.key) {
			const prefix = `QueryOptions Debug - "${this.options.debug.key}": `;
			console[type](prefix, ...messages);
		}
	}
	debug = {
		error: (...messages: any[]) => {
			this.debugBase(DebugType.error, ...messages);
		},
		info: (...messages: any[]) => {
			this.debugBase(DebugType.info, ...messages);
		},
		warn: (...messages: any[]) => {
			this.debugBase(DebugType.warn, ...messages);
		},
	};
	execute(executionOptions: IExecutionOptions) {
		this.debug.info('Execute Started', { options: this.options });
		// in the case that the QueryOptions passed has already been converted, we will just return it as is.
		if (!isQueryOptionsObject(this.options)) {
			this.debug.info('Already converted to a value, options are not of type IQueryOptions');
			return this.options;
		}

		const { queryOptionsType, value, separators = [], keyQuery, codeQuery } = this.options;

		if (keyQuery) {
			this.debug.info('keyQuery defined', { keyQuery });
			const query = new Query(keyQuery, this.translationHelper);
			this.options.key = query.execute(executionOptions);
			this.debug.info('key = ', this.options.key);
		}

		if (codeQuery) {
			this.debug.info('codeQuery defined', { codeQuery });
			const query = new Query(codeQuery, this.translationHelper);
			this.options.code = query.execute(executionOptions);
			this.debug.info('code = ', this.options.code);
		}

		let query: BaseQuery | undefined;

		if (!isUndefined(value)) {
			query = new ValueQuery(this.options, this.translationHelper);
		}
		else if (queryOptionsType) {
			switch (queryOptionsType) {
				case 'response':
					query = new ResponseQuery(this.options, this.translationHelper);
					break;
				case 'dynamic':
					query = new DynamicQuery(this.options, this.translationHelper);
					break;
				case 'input':
					query = new InputQuery(this.options, this.translationHelper);
					break;
				case 'property':
					query = new PropertyQuery(this.options, this.translationHelper);
					break;
				case 'allocations':
					query = new AllocationsQuery(this.options, this.translationHelper);
					break;
				case 'statistics':
					query = new StatisticsQuery(this.options, this.translationHelper);
					break;
				case 'statistic':
					query = new StatisticQuery(this.options, this.translationHelper);
					break;
				case 'timeseries':
					query = new TimeseriesQuery(this.options, this.translationHelper);
					break;
				case 'fundManager':
					query = new FundManagerQuery(this.options, this.translationHelper);
					break;
				case 'fundManagers':
					query = new FundManagersQuery(this.options, this.translationHelper);
					break;
				case 'commentaries':
					query = new CommentaryQuery(this.options, this.translationHelper);
					break;
				case 'disclaimers':
					query = new DisclaimerQuery(this.options, this.translationHelper);
					break;
				case 'documents':
					query = new DocumentQuery(this.options, this.translationHelper);
					break;
				case 'document':
					query = new SingleDocumentQuery(this.options, this.translationHelper);
					break;
				case 'fundLists':
					query = new FundListsQuery(this.options, this.translationHelper);
					break;
				case 'object':
					query = new ObjectQuery(this.options, this.translationHelper);
					break;
				case 'global':
					query = new GlobalQuery(this.options, this.translationHelper);
					break;
				case 'clientConfiguration':
					query = new ClientConfigurationQuery(this.options, this.translationHelper);
					break;
				case 'url':
					query = new UrlQuery(this.options, this.translationHelper);
					break;
				case 'window':
					query = new WindowQuery(this.options, this.translationHelper);
					break;
				case 'join':
					query = new JoinQuery(this.options, this.translationHelper);
					break;
				case 'scenario':
					query = new ScenarioQuery(this.options, this.translationHelper);
					break;
				case 'scenarioReport':
					query = new ScenarioReportQuery(this.options, this.translationHelper);
					break;
				case 'portfolio':
					query = new PortfolioQuery(this.options, this.translationHelper);
					break;
				case 'portfolioEntity':
					query = new PortfolioEntityQuery(this.options, this.translationHelper);
					break;
				case 'asset':
					query = new AssetQuery(this.options, this.translationHelper);
					break;
				case 'scenarioStatistic':
					query = new ScenarioStatisticQuery(this.options, this.translationHelper);
					break;
				case 'entities':
					query = new EntitiesQuery(this.options, this.translationHelper);
					break;
				case 'breakdownAnalysis':
					query = new BreakdownAnalysisQuery(this.options, this.translationHelper);
					break;
				case 'loggedInUser':
					query = new LoggedInUserQuery(this.options, this.translationHelper);
					break;
				case 'externalApi':
					query = new ExternalApiQuery(this.options, this.translationHelper);
					break;
				case 'transactions':
					query = new TransactionsQuery(this.options, this.translationHelper);
					break;
				case 'none':
					query = new ValueQuery(this.options, this.translationHelper);
					break;
			}
		}
		if (query) {
			let queryResponse = query.execute(executionOptions, this.callStackLevel);
			if (queryResponse && typeof queryResponse === 'string' && separators && separators.length > 0) {
				queryResponse = this.stripWrappingSeparators(queryResponse, separators);
			}
			return queryResponse;
		}
	}

	stripWrappingSeparators(queryResponse: string, separators: string[]) {
		for (let separatorIndex = 0; separatorIndex < separators.length; separatorIndex++) {
			const separator = separators[separatorIndex];
			const separtorLength = separator.length;
			while (queryResponse.startsWith(separator)) {
				queryResponse = (queryResponse as string).substr(separtorLength);
			}
			while (queryResponse.endsWith(separator)) {
				queryResponse = (queryResponse as string).substr(0, queryResponse.length - separtorLength);
			}
		}
		return queryResponse;
	}
}